import React from "react";
import { SurveyAnswers, SurveyResponses, useSurvey } from "../../../../common/hooks/api/useSurvey";
import { NetworkRequest, Organization } from "../../../../common/models";
import { useDataMapPendingCollectedPicIds } from "../../../../common/service/resources/data-map/dataMapPending";
import { useDataMapCollectedPICIds } from "../../../../common/service/resources/data-map/dataMapTransformers";
import { CollectionGroupDetailsDto } from "../../../../common/service/server/dto/CollectionGroupDto";
import { DataMapDto } from "../../../../common/service/server/dto/datamap/DataMapDto";
import { SurveyGateButton } from "../../../components/organisms/survey/SurveyGateButton";
import { useFilteredPicGroups } from "./ConsumerDataCollectionCheckboxes";
import { CollectionCheckboxes } from "../../get-compliant/collection-groups/CollectionCheckboxes";
import {
  BUSINESS_CONSUMER_HEALTH_CG_QUESTION_SLUG,
  BUSINESS_CONSUMER_HEALTH_PIC_KEY,
  BUSINESS_SURVEY_NAME,
} from "../../../surveys/businessSurvey";
import { Loading } from "../../../../common/components/Loading";

export const ConsumerDataCollectionForm: React.FC<{
  dataMap: DataMapDto;
  org: Organization;
  collectionGroup: CollectionGroupDetailsDto;
  answers: SurveyResponses;
  setAnswer: (slug: string, answer: string, question?: string) => Promise<SurveyAnswers | void>;
}> = ({ answers, setAnswer, dataMap, org, collectionGroup }) => {
  const orgId = org.id;
  const collected = useDataMapCollectedPICIds(dataMap, { collectionGroupId: collectionGroup.id });
  const pending = useDataMapPendingCollectedPicIds(dataMap, {
    collectionGroupId: collectionGroup.id,
  });
  const filteredPicGroups = useFilteredPicGroups({ dataMap, org });
  const { answers: bizAnswers, request: bizSurveyRequest } = useSurvey(orgId, BUSINESS_SURVEY_NAME);
  if (NetworkRequest.anyRunning(bizSurveyRequest)) {
    return <Loading />;
  }
  const autoselected = [];
  const healthCollectionAnswer = bizAnswers[BUSINESS_CONSUMER_HEALTH_CG_QUESTION_SLUG];
  if (healthCollectionAnswer?.includes(collectionGroup.id)) {
    autoselected.push(BUSINESS_CONSUMER_HEALTH_PIC_KEY);
  }
  return (
    <>
      <CollectionCheckboxes
        group={collectionGroup}
        orgId={orgId}
        categoryGroups={filteredPicGroups.slice(0, 1)}
        collected={collected}
        pending={pending}
      />

      <SurveyGateButton
        slug="common-done"
        survey={answers}
        updateResponse={setAnswer}
        label="Next Categories"
      >
        <CollectionCheckboxes
          group={collectionGroup}
          orgId={orgId}
          categoryGroups={filteredPicGroups.slice(1, 4)}
          collected={collected}
          pending={pending}
        />

        <SurveyGateButton
          slug="sensitive-done"
          survey={answers}
          updateResponse={setAnswer}
          label="Next Categories"
        >
          <CollectionCheckboxes
            group={collectionGroup}
            orgId={orgId}
            categoryGroups={filteredPicGroups.slice(4, 7)}
            collected={collected}
            pending={pending}
          />

          <SurveyGateButton
            slug="gdpr-sensitive-done"
            survey={answers}
            updateResponse={setAnswer}
            label="Next Categories"
          >
            <CollectionCheckboxes
              group={collectionGroup}
              orgId={orgId}
              categoryGroups={filteredPicGroups.slice(7, 9)}
              collected={collected}
              pending={pending}
              autoselected={autoselected}
            />

            <SurveyGateButton
              slug="uncommon-done"
              survey={answers}
              updateResponse={setAnswer}
              label="Next Categories"
            >
              <CollectionCheckboxes
                group={collectionGroup}
                orgId={orgId}
                categoryGroups={filteredPicGroups.slice(9, 12)}
                collected={collected}
                pending={pending}
              />

              <CollectionCheckboxes
                group={collectionGroup}
                orgId={orgId}
                categoryGroups={filteredPicGroups.slice(12)}
                collected={collected}
                pending={pending}
              />

              <SurveyGateButton
                slug="inferences-done"
                survey={answers}
                updateResponse={setAnswer}
                label="Done"
              />
            </SurveyGateButton>
          </SurveyGateButton>
        </SurveyGateButton>
      </SurveyGateButton>
    </>
  );
};
